import React, { useState, useCallback, useEffect } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseForm } from "@app/components/common/forms/BaseForm/BaseForm";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { BasePopconfirm } from "@app/components/common/BasePopconfirm/BasePopconfirm";
import { PageTitle } from "@app/components/common/PageTitle/PageTitle";
import {
  message,
  Modal,
  Input,
  Select,
  Form,
  Row,
  Col,
  Card,
  InputNumber,
  Descriptions,
} from "antd";
import { adminApi, Company, Department, Employee } from "@app/api/admin";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";
import dayjs from "dayjs";
import {
  initialPagination,
  initialPaginationProps,
  useCompanyList,
} from "./CompanyPage";
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useEmployeeList } from "./employee/EmployeePage";
import { ColumnProps } from "antd/lib/table";

const DepartmentPage: React.FC = () => {
  const [createForm] = BaseForm.useForm();
  const [searchForm] = Form.useForm();

  const [tableData, setTableData] = useState<{
    data: Department[];
    pagination: initialPaginationProps;
  }>({
    data: [],
    pagination: initialPagination,
  });
  const [loading, setLoading] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const { employeeList } = useEmployeeList();

  const { companyList } = useCompanyList();

  // 添加搜索参数状态
  const [searchParams, setSearchParams] = useState<{
    name?: string;
  }>({});

  // 添加独立的分页状态
  const [pagination, setPagination] = useState(initialPagination);

  // 获取部门列表数据 -
  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const { total, ...rest } = pagination;
      const { data } = await adminApi.departments.list({
        ...rest,
        ...searchParams,
      });
      setTableData({
        data: data.departments || [],
        pagination: {
          ...pagination,
          total: data.total,
        },
      });
    } catch (error: any) {
      console.log(error);
      // message.error(error.message || "获取部门列表失败");
    } finally {
      setLoading(false);
    }
  };

  useUpdateEffect(() => {
    fetchDepartments();
  }, [searchParams, pagination]);

  // 搜索处理 - 更新为表单提交方式
  const handleSearch = (values: any) => {
    setSearchParams(values);
    setPagination({ ...initialPagination, page: 1 });
  };

  // 重置搜索 - 更新为表单重置方式
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({});
    setPagination({ ...initialPagination, page: 1 });
  };

  // 编辑功能
  const handleEdit = async (record: Department) => {
    try {
      const { data } = await adminApi.departments.detail(record.id);

      // 确保positions字段有默认值
      const formData = {
        ...data,
        company_id: data?.company?.id,
      };

      createForm.setFieldsValue(formData);
      setIsEdit(true);
      setIsCreateModalVisible(true);
    } catch (error: any) {
      console.error("error", error);
      // message.error("获取部门详情失败");
    }
  };

  // 删除
  const handleDeleteRow = async (id: number) => {
    try {
      await adminApi.departments.delete(id);
      fetchDepartments();

      message.success("删除成功");
    } catch (error: any) {
      console.log(error);
      // message.error(error.message || "删除失败");
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({
      page,
      page_size: pageSize,
    });
  };

  const [columns] = useState<ColumnProps<Department>[]>([
    {
      title: "部门名称",
      dataIndex: "name",
      width: 180,
    },
    {
      title: "职位信息",
      dataIndex: "positions",
      width: 200,
      render: (_, record) => {
        if (!record.positions || record.positions.length === 0) {
          return <span style={{ color: "#999" }}>暂无职位</span>;
        }
        return (
          <a
            onClick={() => {
              Modal.info({
                title: record.name + " 职位信息",
                content: (
                  <div style={{ maxHeight: 500, overflowY: "auto" }}>
                    <Descriptions column={1} bordered>
                      {record.positions.map((position, index) => (
                        <Descriptions.Item
                          key={index}
                          label={position.position_name}
                        >
                          {position.employee_id
                            ? position.employee_name
                            : "未分配"}
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                  </div>
                ),
              });
            }}
          >
            查看
          </a>
        );
      },
    },
    {
      title: "员工数量",
      dataIndex: "employee_count",
      width: 100,
      render: (_, record) => record.positions.length || 0,
    },
    {
      title: "公司",
      dataIndex: "company",
      width: 180,
      render: (company: Company) => company?.name || "",
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      width: 180,
      render: (created_at: string) => {
        return dayjs(created_at).format("YYYY-MM-DD HH:mm:ss");
      },
    },
    {
      title: "更新时间",
      dataIndex: "updated_at",
      width: 180,
      render: (updated_at: string) => {
        return dayjs(updated_at).format("YYYY-MM-DD HH:mm:ss");
      },
    },

    {
      title: "操作",
      dataIndex: "actions",
      width: 140,
      fixed: "right" as const,
      render: (_: any, record: Department) => {
        return (
          <BaseSpace>
            <BaseButton
              type="ghost"
              size="small"
              onClick={() => handleEdit(record)}
            >
              编辑
            </BaseButton>
            <BasePopconfirm
              title="确定要删除这个部门吗？"
              onConfirm={() => handleDeleteRow(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <BaseButton type="default" size="small" danger>
                删除
              </BaseButton>
            </BasePopconfirm>
          </BaseSpace>
        );
      },
    },
  ]);

  // 新增部门
  const handleAddDepartment = () => {
    setIsCreateModalVisible(true);
    setIsEdit(false);
    createForm.resetFields();
    // 初始化positions字段为空数组
    createForm.setFieldsValue({
      positions: [],
    });
  };

  // 提交表单
  const handleCreateSubmit = async () => {
    try {
      setCreateLoading(true);
      const values = await createForm.validateFields();

      isEdit
        ? await adminApi.departments.update(values.id, values)
        : await adminApi.departments.create(values);

      fetchDepartments();
      message.success(isEdit ? "编辑成功" : "创建成功");
      setIsCreateModalVisible(false);
      createForm.resetFields();
    } catch (error: any) {
      console.log(error);

      // message.error(error.message || (isEdit ? "编辑失败" : "创建失败"));
    } finally {
      setCreateLoading(false);
    }
  };

  // 关闭弹窗
  const handleCreateCancel = () => {
    setIsCreateModalVisible(false);
    createForm.resetFields();
  };

  return (
    <>
      <PageTitle>部门管理</PageTitle>

      {/* 搜索表单 - 重构为使用 Form 组件 */}
      <Form
        form={searchForm}
        onFinish={handleSearch}
        layout="inline"
        style={{
          marginBottom: 16,
        }}
      >
        <Row gutter={[16, 16]} style={{ width: "100%" }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="name" style={{ marginBottom: 0 }}>
              <Input placeholder="搜索部门名称" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="company_id">
              <Select placeholder="请选择公司">
                {companyList.map((item) => (
                  <Select.Option
                    key={item.id}
                    value={item.id}
                    disabled={isEdit}
                  >
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={16} lg={6}>
            <Form.Item style={{ marginBottom: 0 }}>
              <BaseSpace>
                <BaseButton
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  搜索
                </BaseButton>
                <BaseButton onClick={handleReset}>重置</BaseButton>
                <BaseButton type="primary" onClick={handleAddDepartment}>
                  新增部门
                </BaseButton>
              </BaseSpace>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BaseTable
        bordered
        dataSource={tableData.data}
        columns={columns}
        pagination={{
          ...tableData.pagination,
          onChange: handleTableChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        loading={loading}
        rowKey="id"
        scroll={{ x: 1200 }}
      />

      <Modal
        title={isEdit ? "编辑部门" : "创建部门"}
        visible={isCreateModalVisible}
        onOk={handleCreateSubmit}
        onCancel={handleCreateCancel}
        confirmLoading={createLoading}
        width={800}
        destroyOnClose
      >
        <BaseForm
          form={createForm}
          layout="vertical"
          initialValues={{ positions: [] }}
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <BaseForm.Item label="部门ID" name="id" hidden>
            <Input />
          </BaseForm.Item>
          <BaseForm.Item
            label="部门名称"
            name="name"
            rules={[{ required: true, message: "请输入部门名称" }]}
          >
            <Input placeholder="请输入部门名称" maxLength={50} showCount />
          </BaseForm.Item>
          {/* 部门负责人manager_id */}
          {/* <BaseForm.Item
            label="部门负责人"
            name="manager_id"
            rules={[{ required: true, message: "请选择部门负责人" }]}
          >
            <Select placeholder="请选择部门负责人">
              {employeeList.map((employee) => (
                <Select.Option
                  key={employee.id}
                  value={employee.id}
                >
                  {employee.name}
                </Select.Option>
              ))}
            </Select>
          </BaseForm.Item> */}
          <BaseForm.Item
            name="company_id"
            label="所属公司"
            rules={[{ required: true, message: "请选择公司" }]}
          >
            <Select placeholder="请选择公司" disabled={isEdit}>
              {companyList.map((item) => (
                <Select.Option key={item.id} value={item.id} >
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </BaseForm.Item>

          <BaseForm.Item label="职位管理">
            <BaseForm.List name="positions">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      title={`职位 ${name + 1}`}
                      extra={
                        <BaseButton
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        >
                          删除
                        </BaseButton>
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <BaseForm.Item
                            {...restField}
                            name={[name, "position_name"]}
                            label="职位名称"
                            rules={[
                              { required: true, message: "请输入职位名称" },
                            ]}
                          >
                            <Input placeholder="请输入职位名称" />
                          </BaseForm.Item>
                        </Col>
                        <Col span={12}>
                          <BaseForm.Item
                            {...restField}
                            name={[name, "employee_id"]}
                            label="员工"
                            rules={[{ required: true, message: "请选择员工" }]}
                          >
                            <Select placeholder="请选择员工" allowClear>
                              {employeeList.map((employee) => (
                                <Select.Option
                                  key={employee.id}
                                  value={employee.id}
                                >
                                  {employee.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </BaseForm.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={18}>
                          <BaseForm.Item
                            {...restField}
                            name={[name, "description"]}
                            label="职位描述"
                          >
                            <Input.TextArea
                              placeholder="请输入职位描述"
                              rows={2}
                              maxLength={200}
                              showCount
                            />
                          </BaseForm.Item>
                        </Col>
                        <Col span={6}>
                          <BaseForm.Item
                            {...restField}
                            name={[name, "display_order"]}
                            label="显示顺序"
                            rules={[
                              { required: true, message: "请输入显示顺序" },
                            ]}
                          >
                            <InputNumber
                              placeholder="显示顺序"
                              min={1}
                              max={999}
                              style={{ width: "100%" }}
                            />
                          </BaseForm.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <BaseButton
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加职位
                  </BaseButton>
                </>
              )}
            </BaseForm.List>
          </BaseForm.Item>
        </BaseForm>
      </Modal>
    </>
  );
};

export default DepartmentPage;

export const useDepartment = () => {
  const [departmentList, setDepartmentList] = useState<Department[]>([]);

  const fetchDepartments = async () => {
    try {
      const response = await adminApi.departments.list({
        page: 1,
        page_size: 100,
      });
      setDepartmentList(response.data.departments || []);
    } catch (error: any) {
      console.error(error.message || "获取部门列表失败");
    }
  };

  useUpdateEffect(() => {
    fetchDepartments();
  }, []);

  return {
    departmentList,
    fetchDepartments,
  };
};
